import type {
  AccountId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import { MongoTeamId } from "@redotech/effect-api-shared/ids/mongo-ids";
import { useSupportTicket } from "./use-support-ticket-data";

/**
 * Read the ticket's owning Mongo team id. Per-team lookups
 * (assignee/customer pickers, related records) should derive the team this
 * way rather than accept a `teamId` prop, keeping the scope colocated with
 * the supportTicketId the component already has.
 *
 * Backed by {@link useSupportTicket}, which subscribes to the in-memory list
 * collection AND triggers a single-ticket fetch when the row is absent. On
 * cold deep-link (PRO-1069: ticket isn't in any loaded list page), the
 * fetch fills the collection and the value resolves. Callers receive a
 * `{ value, isLoading }` shape so they can distinguish "fetch pending"
 * from "ticket truly has no team" and avoid collapsing dependent UI into
 * an empty/disabled state during the race window.
 */
export function useSupportTicketTeamId(supportTicketId: SupportTicketId): {
  teamId: string | undefined;
  isLoading: boolean;
} {
  const { data, isLoading } = useSupportTicket(supportTicketId);
  const teamId = data?.teamId;
  return {
    teamId: teamId ? MongoTeamId.toRawId(teamId) : undefined,
    isLoading,
  };
}

/**
 * Read the ticket's owning CRDB account id. Same lookup as
 * {@link useSupportTicketTeamId} — used by pickers whose backing data is
 * account-keyed in CRDB (e.g. the tag picker, where tag rows belong to an
 * account_id).
 */
export function useSupportTicketAccountId(supportTicketId: SupportTicketId): {
  accountId: AccountId | undefined;
  isLoading: boolean;
} {
  const { data, isLoading } = useSupportTicket(supportTicketId);
  return { accountId: data?.accountId, isLoading };
}
